import { useCallback, useRef, useState } from 'react';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { applyRowSelectionRange, selectedRowIds } from '../utils/row-selection-range';

type UseShiftClickRowSelectionOptions = {
  getVisibleRowIds: () => string[];
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
};

function resolveNextSelection(
  previous: RowSelectionState,
  updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)
): RowSelectionState {
  return typeof updater === 'function' ? updater(previous) : updater;
}

export function useShiftClickRowSelection({
  getVisibleRowIds,
  rowSelection: controlledSelection,
  onRowSelectionChange,
}: UseShiftClickRowSelectionOptions): {
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  selectedIds: string[];
  selectRangeTo: (rowId: string) => void;
  toggleRowSelected: (rowId: string, selected: boolean) => void;
  clearSelection: () => void;
  setAnchorRowId: (rowId: string | null) => void;
  handleRowClick: (
    rowId: string,
    event: { shiftKey: boolean; preventDefault: () => void },
    onPlainClick: () => void
  ) => void;
} {
  const [uncontrolledSelection, setUncontrolledSelection] = useState<RowSelectionState>({});
  const isControlled = controlledSelection !== undefined && onRowSelectionChange !== undefined;
  const rowSelection = isControlled ? controlledSelection : uncontrolledSelection;
  const selectionAnchorIdRef = useRef<string | null>(null);
  const getVisibleRowIdsRef = useRef(getVisibleRowIds);
  getVisibleRowIdsRef.current = getVisibleRowIds;

  const setRowSelection = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      if (isControlled) {
        onRowSelectionChange(updater);
        return;
      }
      setUncontrolledSelection((previous) => resolveNextSelection(previous, updater));
    },
    [isControlled, onRowSelectionChange]
  );

  const selectRangeTo = useCallback(
    (rowId: string): void => {
      const visibleIds = getVisibleRowIdsRef.current();
      const currentIndex = visibleIds.indexOf(rowId);
      if (currentIndex < 0) {
        return;
      }
      const anchorId = selectionAnchorIdRef.current;
      const anchorIndex = anchorId ? visibleIds.indexOf(anchorId) : -1;
      if (anchorIndex < 0) {
        setRowSelection((prev) => ({ ...prev, [rowId]: true }));
        selectionAnchorIdRef.current = rowId;
        return;
      }
      setRowSelection((prev) =>
        applyRowSelectionRange(prev, visibleIds, anchorIndex, currentIndex)
      );
    },
    [setRowSelection]
  );

  const toggleRowSelected = useCallback(
    (rowId: string, selected: boolean): void => {
      setRowSelection((prev) => {
        const next: RowSelectionState = { ...prev };
        if (selected) {
          next[rowId] = true;
        } else {
          delete next[rowId];
        }
        return next;
      });
      selectionAnchorIdRef.current = rowId;
    },
    [setRowSelection]
  );

  const clearSelection = useCallback((): void => {
    selectionAnchorIdRef.current = null;
    setRowSelection({});
  }, [setRowSelection]);

  const setAnchorRowId = useCallback((rowId: string | null): void => {
    selectionAnchorIdRef.current = rowId;
  }, []);

  const handleRowClick = useCallback(
    (
      rowId: string,
      event: { shiftKey: boolean; preventDefault: () => void },
      onPlainClick: () => void
    ): void => {
      if (event.shiftKey) {
        event.preventDefault();
        selectRangeTo(rowId);
        return;
      }
      onPlainClick();
    },
    [selectRangeTo]
  );

  return {
    rowSelection,
    setRowSelection,
    selectedIds: selectedRowIds(rowSelection),
    selectRangeTo,
    toggleRowSelected,
    clearSelection,
    setAnchorRowId,
    handleRowClick,
  };
}
